import React, { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import Wordmark from "@/components/Wordmark";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFab from "@/components/WhatsAppFab";
import { useCart } from "@/lib/CartContext";
import { Menu, X, Instagram, Youtube, ShoppingBag, MapPin } from "lucide-react";
import { usePageTracker } from "@/lib/analytics";
import { GMB_URL, INSTAGRAM_URL } from "@/components/SEO";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/kits", label: "Puja Kits" },
  { to: "/calendar", label: "Calendar" },
  { to: "/about", label: "About" },
  { to: "/retailer", label: "Retailer" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#FBF6EC]/95 backdrop-blur-md border-b border-[#3B2A1A]/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20 sm:h-24">
        <Link to="/" data-testid="header-logo-link">
          <Wordmark />
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.18em] font-semibold hover-underline ${
                  isActive ? "text-[#C89D3C]" : "text-[#3B2A1A]"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-1">
          <button
            data-testid="header-cart-btn"
            onClick={() => setCartOpen(true)}
            aria-label="Open cart"
            className="relative p-2.5 text-[#3B2A1A] hover:text-[#C89D3C] transition-colors"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                data-testid="header-cart-count"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#C89D3C] text-white text-[10px] font-semibold rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#3B2A1A] p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#3B2A1A]/10 bg-[#FBF6EC]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="text-sm uppercase tracking-[0.18em] font-semibold text-[#3B2A1A] py-2"
              >
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#3B2A1A] text-[#FBF6EC] mt-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
      <div>
        <Logo inverted />
        <p className="text-sm leading-relaxed text-[#E2C37B] mt-6 max-w-xs">
          Pure ritual essentials crafted with authenticity, quality and trust for every Indian home.
        </p>
      </div>

      <div>
        <h4 className="overline mb-5">Explore</h4>
        <ul className="space-y-3 text-sm">
          <li><Link data-testid="footer-link-products" to="/products" className="hover-underline">Products</Link></li>
          <li><Link data-testid="footer-link-kits" to="/kits" className="hover-underline">Puja Kits</Link></li>
          <li><Link data-testid="footer-link-about" to="/about" className="hover-underline">About</Link></li>
          <li><Link data-testid="footer-link-blog" to="/blog" className="hover-underline">Journal</Link></li>
        </ul>
      </div>

      <div>
        <h4 className="overline mb-5">Partner</h4>
        <ul className="space-y-3 text-sm">
          <li><Link data-testid="footer-link-retailer" to="/retailer" className="hover-underline">Retailer</Link></li>
          <li><Link data-testid="footer-link-distributor" to="/distributor" className="hover-underline">Distributor</Link></li>
          <li><Link data-testid="footer-link-contact" to="/contact" className="hover-underline">Contact</Link></li>
          <li><a data-testid="footer-link-indiamart" href="https://www.indiamart.com/saragreenworksprivatelimited-newdelhi/" target="_blank" rel="noopener noreferrer" className="hover-underline">Buy on IndiaMART</a></li>
        </ul>
      </div>

      <div>
        <h4 className="overline mb-5">Reach Us</h4>
        <p className="text-sm text-[#FBF6EC]/85">A Unit of Sara Greenworks Pvt. Ltd.</p>
        <p className="text-sm text-[#FBF6EC]/85 mt-3 leading-relaxed">
          Second Floor, Kh No. 26/3,<br/>
          Main Seva Dhaam Road, Mandoli,<br/>
          Delhi — 110093
        </p>
        <p className="text-sm text-[#FBF6EC]/85 mt-3">info@sanatandharmaonline.com</p>
        <p className="text-sm text-[#FBF6EC]/85">+91-7982444690</p>
        <a
          href={GMB_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-gmb-link"
          className="inline-flex items-center gap-1.5 mt-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#E2C37B] hover:text-white transition-colors"
        >
          <MapPin size={12} /> Find Us On Google
        </a>
        <div className="flex items-center gap-4 mt-6">
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[#E2C37B] hover:text-[#FBF6EC]" data-testid="social-instagram"><Instagram size={18} /></a>
          <a href="https://www.youtube.com/@sanatandharma_online" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-[#E2C37B] hover:text-[#FBF6EC]" data-testid="social-youtube"><Youtube size={18} /></a>
          <a href={GMB_URL} target="_blank" rel="noopener noreferrer" aria-label="Google Business Profile" className="text-[#E2C37B] hover:text-[#FBF6EC]" data-testid="social-gmb"><MapPin size={18} /></a>
        </div>
      </div>
    </div>
    <div className="border-t border-[#FBF6EC]/15">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-[#E2C37B]">
        <p>© {new Date().getFullYear()} Sanatan Dharma™ — A Unit of Sara Greenworks Pvt. Ltd.</p>
        <p>Made In India · Crafted For Every Home</p>
      </div>
    </div>
  </footer>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const Layout = () => {
  usePageTracker();
  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B2A1A]">
      <ScrollToTop />
      <Header />
      <main><Outlet /></main>
      <Footer />
      <CartDrawer />
      <WhatsAppFab />
    </div>
  );
};

export default Layout;
